import { useEffect, useState } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { LocalAiLeadMapping } from "@/api/admin-api"
import type { DirectorySearchResult } from "@/services/directory-search-service"

import { upsertLocalAiLead } from "@/api/admin-api"
import { DirectorySearch } from "@/components/directory-search/directory-search"
import { X } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import {
  DIRECTORY_GROUP_LIST_MAX_TOP,
  DirectorySearchService,
  getDirectoryGroupValue,
} from "@/services/directory-search-service"
import { SettingsService } from "@/services/settings-service"

import { FilteredDirectoryList } from "./filtered-directory-list"

type SelectedUser = {
  oid: string
  name: string
  email: string
  /** Value of the configured `group_attribute` (e.g. department) at selection time. */
  groupValue?: string
}

type AddLocalAiLeadDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  editing?: LocalAiLeadMapping | null
  /** Overrides the grouping attribute. When provided, the dialog uses this
   *  instead of the (potentially stale) SettingsService cache. */
  groupAttribute?: string
  /** When set, scopes all directory searches to transitive members of this
   *  Azure AD group (the platform-access group). */
  scopeGroup?: string
}

function toSelectedUser(r: DirectorySearchResult, groupAttribute: string): SelectedUser {
  return {
    oid: r.oid,
    name: r.displayName,
    email: r.email ?? "",
    groupValue: getDirectoryGroupValue(r, groupAttribute),
  }
}

function useUpsertLocalAiLeadMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      oid,
      body,
    }: {
      oid: string
      body: {
        ambassadorName: string
        ambassadorEmail: string
        assignedUsers: Array<{ oid: string; name: string; email: string }>
        assignedGroups?: string[]
      }
    }) => upsertLocalAiLead(oid, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "localAiLeads"] })
    },
  })
}

export function AddLocalAiLeadDialog({
  open,
  onOpenChange,
  editing,
  groupAttribute: groupAttributeProp,
  scopeGroup,
}: AddLocalAiLeadDialogProps) {
  const { t } = useTranslation()
  const upsertMutation = useUpsertLocalAiLeadMutation()

  // Prefer the prop (which comes from the freshly-fetched config query in the parent)
  // over the SettingsService cache which may be stale after an admin saves a new attribute.
  const groupAttribute =
    groupAttributeProp ??
    SettingsService.getInstance().settings.local_ai_lead_config?.group_attribute ??
    "department"

  // NOTE: document field names (ambassadorOid/Name/Email) are preserved on the
  // stored Cosmos schema; only the variable name was rebranded.
  const [lead, setLead] = useState<SelectedUser | null>(
    editing
      ? {
          oid: editing.ambassadorOid,
          name: editing.ambassadorName,
          email: editing.ambassadorEmail,
        }
      : null
  )
  const [assignedUsers, setAssignedUsers] = useState<SelectedUser[]>(
    editing ? editing.assignedUsers : []
  )
  const [groupFilter, setGroupFilter] = useState("")
  const [assignedGroupsText, setAssignedGroupsText] = useState(
    editing?.assignedGroups?.join(", ") ?? ""
  )

  const isEditing = !!editing

  // When editing, the Local AI Lead comes from Cosmos which doesn't store the
  // Entra group attribute value. Resolve it so the quick-add CTA can show
  // "Local AI Lead is in {department}".
  useEffect(() => {
    if (!isEditing || !lead || lead.groupValue) return
    DirectorySearchService.searchDirectory({
      query: lead.name,
      types: ["users"],
      top: 5,
      admin: true,
    })
      .then((results) => {
        const match = results.find((r) => r.oid === lead.oid)
        if (match) {
          const resolved = getDirectoryGroupValue(match, groupAttribute)
          if (resolved) {
            setLead((prev) => (prev ? { ...prev, groupValue: resolved } : prev))
          }
        }
      })
      .catch(() => {
        // Non-critical — quick-add CTA just won't show
      })
  }, [isEditing, lead?.oid, lead?.groupValue, lead?.name, groupAttribute])

  const handleLeadSelect = (result: DirectorySearchResult) => {
    setLead(toSelectedUser(result, groupAttribute))
  }

  const handleAssignedUserSelect = (result: DirectorySearchResult) => {
    const user = toSelectedUser(result, groupAttribute)
    setAssignedUsers((prev) => {
      if (prev.some((u) => u.oid === user.oid)) return prev
      return [...prev, user]
    })
  }

  const handleRemoveAssigned = (oid: string) => {
    setAssignedUsers((prev) => prev.filter((u) => u.oid !== oid))
  }

  const mergeUsers = (
    newResults: DirectorySearchResult[],
    options?: { includeLeadIfMissing?: boolean }
  ): number => {
    const existingOids = new Set(assignedUsers.map((u) => u.oid))
    let toAdd = newResults
      .filter((r) => !existingOids.has(r.oid))
      .map((r) => toSelectedUser(r, groupAttribute))

    if (
      options?.includeLeadIfMissing &&
      lead &&
      !existingOids.has(lead.oid) &&
      !toAdd.some((u) => u.oid === lead.oid)
    ) {
      toAdd = [
        ...toAdd,
        {
          oid: lead.oid,
          name: lead.name,
          email: lead.email,
          groupValue: lead.groupValue,
        },
      ]
    }

    if (toAdd.length > 0) {
      setAssignedUsers((prev) => [...prev, ...toAdd])
    }
    return toAdd.length
  }

  const handleQuickAddByGroup = async (value: string) => {
    try {
      const results = await DirectorySearchService.listByGroupAttribute({
        attribute: groupAttribute,
        value,
        admin: true,
        top: DIRECTORY_GROUP_LIST_MAX_TOP,
        scopeGroup,
      })
      const added = mergeUsers(results, { includeLeadIfMissing: true })
      if (added === 0) {
        toast.info(
          t("adminDashboard.localAiLeads.quickAddNoNew", "No new users to add from {{value}}.", {
            value,
          })
        )
        return
      }
      toast.success(
        t(
          "adminDashboard.localAiLeads.quickAddAddedToast",
          "Added {{count}} users from {{value}}.",
          { count: added, value }
        )
      )
    } catch (error) {
      toast.error(
        t(
          "adminDashboard.localAiLeads.quickAddErrorToast",
          "Failed to load users from {{value}}.",
          {
            value,
          }
        ),
        { description: error instanceof Error ? error.message : String(error) }
      )
    }
  }

  const handleSave = async () => {
    if (!lead) return
    try {
      await upsertMutation.mutateAsync({
        oid: lead.oid,
        body: {
          ambassadorName: lead.name,
          ambassadorEmail: lead.email,
          assignedUsers,
          assignedGroups: [
            ...new Set(
              assignedGroupsText
                .split(",")
                .map((group) => group.trim())
                .filter(Boolean)
            ),
          ],
        },
      })
      toast.success(
        isEditing
          ? t("adminDashboard.localAiLeads.updatedToast", "Local AI Lead updated")
          : t("adminDashboard.localAiLeads.addedToast", "Local AI Lead added")
      )
      onOpenChange(false)
    } catch (error) {
      toast.error(
        isEditing
          ? t("adminDashboard.localAiLeads.updateErrorToast", "Failed to update Local AI Lead")
          : t("adminDashboard.localAiLeads.addErrorToast", "Failed to add Local AI Lead"),
        { description: error instanceof Error ? error.message : String(error) }
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? t("adminDashboard.localAiLeads.editTitle", "Edit Local AI Lead")
              : t("adminDashboard.localAiLeads.addTitle", "Add Local AI Lead")}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="max-h-[60vh] space-y-5 py-2">
          <div className="space-y-2">
            <Label htmlFor="local-ai-lead-company-grants">
              {t("adminDashboard.localAiLeads.companyGrants", "Companies this lead may view")}
            </Label>
            <Input
              id="local-ai-lead-company-grants"
              value={assignedGroupsText}
              placeholder={t(
                "adminDashboard.localAiLeads.companyGrantsPlaceholder",
                "Company names, separated by commas"
              )}
              onChange={(event) => setAssignedGroupsText(event.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              {t(
                "adminDashboard.localAiLeads.companyGrantsHelp",
                "Each grant allows this lead to see the full company's cost. Use values from the configured grouping attribute."
              )}
            </p>
          </div>
          {/* Local AI Lead picker */}
          <div className="space-y-2">
            <Label>{t("adminDashboard.localAiLeads.leadLabel", "Local AI Lead")}</Label>
            {lead ? (
              <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                <div>
                  <span className="font-medium">{lead.name}</span>
                  {lead.email && (
                    <span className="text-muted-foreground ml-2 text-xs">{lead.email}</span>
                  )}
                </div>
                {!isEditing && (
                  <button
                    className="text-muted-foreground hover:text-foreground"
                    type="button"
                    onClick={() => setLead(null)}
                  >
                    <X className="size-4" />
                  </button>
                )}
              </div>
            ) : (
              <DirectorySearch
                admin
                groupAttribute={groupAttribute}
                scopeGroup={scopeGroup}
                types={["users"]}
                placeholder={t(
                  "adminDashboard.localAiLeads.leadPlaceholder",
                  "Search for Local AI Lead..."
                )}
                onSelect={handleLeadSelect}
              />
            )}
          </div>

          {/* Assigned users picker */}
          <div className="space-y-2">
            <Label>{t("adminDashboard.localAiLeads.assignedUsersLabel", "Assigned Users")}</Label>
            {assignedUsers.length > 0 && (
              <div className="mb-2 max-h-[min(160px,_42dvh)] overflow-y-auto rounded-md">
                <div className="flex flex-wrap gap-1 p-0.5">
                  {assignedUsers.map((u) => (
                    <Badge key={u.oid} className="gap-1 pr-1 text-xs" variant="secondary">
                      {u.name}
                      <button
                        className="hover:text-destructive ml-1"
                        type="button"
                        onClick={() => handleRemoveAssigned(u.oid)}
                      >
                        <X className="size-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Quick-add CTA: surfaces when the Local AI Lead has a non-empty group value */}
            {lead?.groupValue && !groupFilter && (
              <div className="bg-muted/40 flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                <span className="min-w-0 truncate pr-2">
                  {t(
                    "adminDashboard.localAiLeads.quickAddHint",
                    "Local AI Lead is in {{value}}. Add everyone from the same {{attr}}?",
                    { value: lead.groupValue, attr: groupAttribute }
                  )}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void handleQuickAddByGroup(lead.groupValue!)}
                >
                  {t("adminDashboard.localAiLeads.quickAddButton", "Quick-add")}
                </Button>
              </div>
            )}

            {/* Manual filter by grouping attribute */}
            <div className="flex items-center gap-2">
              <Input
                className="h-8"
                value={groupFilter}
                placeholder={t(
                  "adminDashboard.localAiLeads.filterByAttr",
                  "Filter by {{attr}}...",
                  {
                    attr: groupAttribute,
                  }
                )}
                onChange={(e) => setGroupFilter(e.target.value)}
              />
              {groupFilter && (
                <Button size="sm" variant="ghost" onClick={() => setGroupFilter("")}>
                  {t("common.clear", "Clear")}
                </Button>
              )}
            </div>

            {groupFilter.trim() ? (
              <FilteredDirectoryList
                attribute={groupAttribute}
                bulkAddLabel={t("adminDashboard.localAiLeads.addAllVisible", "Add all visible")}
                scopeGroup={scopeGroup}
                selectedOids={assignedUsers.map((u) => u.oid)}
                value={groupFilter.trim()}
                onSelect={handleAssignedUserSelect}
                onBulkAdd={(results) => {
                  const added = mergeUsers(results)
                  if (added === 0) {
                    toast.info(
                      t(
                        "adminDashboard.localAiLeads.quickAddNoNew",
                        "No new users to add from {{value}}.",
                        { value: groupFilter.trim() }
                      )
                    )
                  } else {
                    toast.success(
                      t(
                        "adminDashboard.localAiLeads.quickAddAddedToast",
                        "Added {{count}} users from {{value}}.",
                        { count: added, value: groupFilter.trim() }
                      )
                    )
                  }
                }}
              />
            ) : (
              <DirectorySearch
                admin
                groupAttribute={groupAttribute}
                selectedOids={assignedUsers.map((u) => u.oid)}
                types={["users"]}
                placeholder={t(
                  "adminDashboard.localAiLeads.assignedUsersPlaceholder",
                  "Search users to assign..."
                )}
                onSelect={handleAssignedUserSelect}
              />
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={!lead || upsertMutation.isPending} onClick={() => void handleSave()}>
            {upsertMutation.isPending ? <Spinner className="mr-2 size-4" /> : null}
            {t("common.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
